import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Box, 
    Typography, 
    Paper,
    CircularProgress, 
    Alert,
    IconButton
} from '@mui/material';
import { 
    Favorite, 
    FavoriteBorder, 
    ArrowBack
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { userRequest } from '../../requestMethods';
import { ShortStoryInteractiveTextViewer } from '../InteractiveText';
import { TappedWordsWidget } from '../Learning';
import PropTypes from 'prop-types';

const ShortStoryView = ({ storyId: propStoryId }) => {
    const { id: paramStoryId } = useParams();
    const storyId = propStoryId || paramStoryId;
    const navigate = useNavigate();
    
    const [story, setStory] = useState(null);
    const [formattedText, setFormattedText] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isFavorite, setIsFavorite] = useState(false);
    const [favoriteLoading, setFavoriteLoading] = useState(false);
    const [tappedWords, setTappedWords] = useState([]);

    const fetchStoryAndText = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            // Fetch story data
            const storyResponse = await userRequest.get(`/contentShortStory/${storyId}`);
            const storyData = storyResponse.data;
            setStory(storyData);

            // Fetch formatted text using the text ID
            if (storyData.text?._id) {
                const textResponse = await userRequest.get(`/text/formatted/byText/${storyData.text._id}?storyId=${storyId}`);
                setFormattedText(textResponse.data);
            }

        } catch (error) {
            console.error('Error fetching story:', error);
            setError('Failed to load story. Please try again later.');
        } finally {
            setLoading(false);
        }
    }, [storyId]);

    const checkFavoriteStatus = useCallback(async () => {
        if (!storyId) return;
        
        try {
            const response = await userRequest.get(`/user/favorites/check/ContentShortStory/${storyId}`);
            setIsFavorite(response.data.isFavorite);
        } catch (error) {
            console.error('Error checking favorite status:', error);
        }
    }, [storyId]);

    useEffect(() => {
        if (storyId) {
            fetchStoryAndText();
            checkFavoriteStatus();
        }
    }, [storyId, fetchStoryAndText, checkFavoriteStatus]);

    const handleToggleFavorite = async () => {
        if (!storyId || favoriteLoading) return;
        
        setFavoriteLoading(true);
        try {
            const response = await userRequest.post('/user/favorites/toggle', {
                contentType: 'ContentShortStory',
                contentId: storyId
            });
            setIsFavorite(response.data.isFavorite);
        } catch (error) {
            console.error('Error toggling favorite:', error);
        } finally {
            setFavoriteLoading(false);
        }
    };

    const handleWordDisplayed = (word) => {
        // Only add words that have translation or grammar link
        if ((word.t && word.t.trim() !== '') || // word.translation
            (word.l && word.l.trim() !== '')) { // word.link
            
            setTappedWords(prevWords => {
                // Check if word already exists (avoid duplicates)
                const wordKey = word.w;
                const exists = prevWords.some(w => 
                    w.w === wordKey
                );
                
                if (!exists) {
                    // Add new word to the beginning of the array
                    return [word, ...prevWords];
                }
                return prevWords;
            });
        }
    };

    const renderTextContent = (textData) => {
        if (!textData) return null;
        
        return (
            <Box>
                {textData.formatted ? (
                    <ShortStoryInteractiveTextViewer 
                        formattedText={textData.text}
                        textId={textData.id}
                        onWordDisplayed={handleWordDisplayed}
                    />
                ) : (
                    <Typography 
                        sx={{
                            lineHeight: 1.8,
                            fontSize: '1.1rem',
                            fontFamily: 'Georgia, serif',
                            whiteSpace: 'pre-wrap',
                            textAlign: 'justify'
                        }}
                    >
                        {textData.text}
                    </Typography>
                )}
            </Box>
        );
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 3 }}>
                {error}
            </Alert>
        );
    }

    if (!story) {
        return (
            <Alert severity="info" sx={{ mb: 3 }}>
                Story not found.
            </Alert>
        );
    }

    const storyContent = formattedText 
        ? renderTextContent(formattedText)
        : (story.text?.text ? (
            <Typography 
                sx={{
                    lineHeight: 1.8,
                    fontSize: '1.1rem',
                    fontFamily: 'Georgia, serif',
                    whiteSpace: 'pre-wrap',
                    textAlign: 'justify'
                }}
            >
                {story.text.text}
            </Typography>
        ) : 'Story content not available.');

    return (
        <Box sx={{ pb: 4 }}>
            {/* Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <IconButton onClick={() => navigate(-1)}>
                    <ArrowBack />
                </IconButton>
                <Typography variant="h5" fontWeight="bold">
                    {story.title}
                </Typography>
                <IconButton
                    onClick={handleToggleFavorite}
                    disabled={favoriteLoading}
                    sx={{ 
                        color: isFavorite ? 'secondary.main' : 'text.secondary',
                    }}
                >
                    {favoriteLoading ? (
                        <CircularProgress size={24} />
                    ) : isFavorite ? (
                        <Favorite />
                    ) : (
                        <FavoriteBorder />
                    )}
                </IconButton>
            </Box>

            {/* Story Thumbnail Image */}
            {story.thumbUrl && (
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    mb: 3 
                }}>
                    <Box
                        component="img"
                        src={story.thumbUrl}
                        alt={story.title}
                        sx={{
                            maxWidth: { xs: '100%', md: '400px' },
                            width: 'auto',
                            height: 'auto',
                            objectFit: 'contain',
                            borderRadius: 2,
                            boxShadow: 2
                        }}
                    />
                </Box>
            )}

            {/* Main Content Area - One Long Scrollable Page */}
            <Box sx={{ 
                display: 'flex', 
                gap: 3,
                flexDirection: { xs: 'column', lg: 'row' }
            }}>
                {/* Main Content Column */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Story Content */}
                    <Paper sx={{ p: 4, mb: 3 }}>
                        {storyContent}
                    </Paper>
                </Box>

                {/* Sidebar - New Words Widget (always visible) */}
                <Box sx={{ 
                    width: { lg: '350px' },
                    display: { xs: 'none', lg: 'block' },
                    flexShrink: 0,
                    position: 'sticky',
                    top: 80,
                    alignSelf: 'flex-start'
                }}>
                    <TappedWordsWidget 
                        tappedWords={tappedWords}
                        variant="inline"
                    />
                </Box>
            </Box>

            {/* Mobile New Words Widget (always visible) */}
            <Box sx={{ display: { xs: 'block', lg: 'none' }, mt: 3 }}>
                <TappedWordsWidget 
                    tappedWords={tappedWords}
                    variant="inline"
                />
            </Box>
        </Box>
    );
};

ShortStoryView.propTypes = {
    storyId: PropTypes.string
};

export default ShortStoryView;
